import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import { 
  Calendar, Search, Bell, MapPin, Music, CheckCircle2, 
  Guitar, ArrowRight, Send, Mic2, Users, Star
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/2197bd5f-e421-4534-9309-6e77e73c8080/images/f414455e9d1ce509b5159e7d208b2595b95b191dd6a4b86a756406370fb51222.png";
const SEARCH_IMG = "https://static.prod-images.emergentagent.com/jobs/2197bd5f-e421-4534-9309-6e77e73c8080/images/419be1760bfa07cf5f0ff56276cbde671cd63fa8ad76d290676d10752cf79491.png";

export default function LandingBandas() {
  const features = [
    {
      icon: Guitar,
      title: "PERFIL DE BANDA",
      description: "Crea un perfil profesional con bio, fotos, géneros musicales y links a tus redes sociales."
    },
    {
      icon: Search,
      title: "BUSCA LOCALES",
      description: "Encuentra bares y salas que buscan bandas de tu estilo. Filtra por ciudad, género y disponibilidad."
    },
    {
      icon: Send,
      title: "PROPÓN CONCIERTOS",
      description: "Envía solicitudes directas a los locales. Define fecha, título del evento y detalles."
    },
    {
      icon: Calendar,
      title: "GESTIONA TU AGENDA",
      description: "Marca las fechas en las que estáis disponibles para tocar. Los bares verán vuestra disponibilidad."
    },
    {
      icon: Bell,
      title: "NOTIFICACIONES",
      description: "Recibe emails cuando un bar apruebe o rechace tu solicitud. Siempre informados."
    },
    {
      icon: Mic2,
      title: "VENUES EXTERNOS",
      description: "¿Tienes un bolo en un sitio no registrado? Puedes añadir venues personalizados."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "REGÍSTRATE",
      description: "Crea tu cuenta de banda en menos de 2 minutos. Es gratis."
    },
    {
      number: "02",
      title: "COMPLETA TU PERFIL",
      description: "Añade foto, bio, géneros y tus fechas disponibles."
    },
    {
      number: "03",
      title: "BUSCA LOCALES",
      description: "Encuentra bares que buscan bandas de tu estilo."
    },
    {
      number: "04",
      title: "PROPÓN Y TOCA",
      description: "Envía tu propuesta y espera la aprobación del bar."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-primary/20 text-primary font-mono text-sm mb-6">
                PARA BANDAS Y ARTISTAS
              </span>
              <h1 className="text-4xl lg:text-6xl font-black text-white mb-6 leading-tight">
                ENCUENTRA DONDE <span className="text-primary">TOCAR</span> TU MÚSICA
              </h1>
              <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
                Conecta con bares y salas que buscan bandas de rock y metal. Propón fechas, gestiona tu agenda y llena los escenarios.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register?type=band">
                  <Button className="bg-primary hover:bg-red-700 text-white font-mono text-lg px-8 py-6" data-testid="band-register-btn">
                    REGISTRAR MI BANDA
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login?type=band">
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 font-mono text-lg px-8 py-6">
                    YA TENGO CUENTA
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
              <img 
                src={HERO_IMG} 
                alt="Dashboard de banda en Rockmaps" 
                className="relative rounded-lg border border-zinc-800 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-[#111]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              TODO PARA TU BANDA
            </h2>
            <p className="text-zinc-500 font-mono max-w-2xl mx-auto">
              Herramientas gratuitas para gestionar tu carrera musical
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#0a0a0a] border border-zinc-800 p-6 hover:border-primary/50 transition-colors group"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-6">
                BUSCA LOCALES QUE ENCAJEN CON TU ESTILO
              </h2>
              <ul className="space-y-4">
                {[
                  "Filtra por ciudad y género musical",
                  "Ve qué fechas tienen disponibles los bares",
                  "Consulta las condiciones de reserva",
                  "Contacta directamente desde la plataforma",
                  "Propón tu concierto en un clic"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register?type=band" className="inline-block mt-8">
                <Button className="bg-primary hover:bg-red-700 text-white font-mono">
                  EMPEZAR A BUSCAR LOCALES
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div>
              <img 
                src={SEARCH_IMG} 
                alt="Búsqueda de locales en Rockmaps" 
                className="rounded-lg border border-zinc-800 shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 bg-[#111]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              CÓMO FUNCIONA
            </h2>
            <p className="text-zinc-500 font-mono">
              4 pasos para conseguir tu próximo bolo
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="bg-[#0a0a0a] border border-zinc-800 p-6 relative overflow-hidden group hover:border-primary/50 transition-colors"
                data-testid={`step-${index}`}
              >
                <span className="absolute -top-4 -right-4 text-8xl font-black text-zinc-900 group-hover:text-primary/20 transition-colors">
                  {step.number}
                </span>
                <div className="relative z-10">
                  <span className="text-primary font-mono text-sm">{step.number}</span>
                  <h3 className="text-xl font-bold text-white mt-2 mb-2">{step.title}</h3>
                  <p className="text-zinc-500 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial/Stats Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-8">
              <div className="text-5xl font-black text-primary mb-2">100%</div>
              <div className="text-zinc-400 font-mono">GRATIS PARA BANDAS</div>
            </div>
            <div className="p-8">
              <div className="text-5xl font-black text-primary mb-2">24h</div>
              <div className="text-zinc-400 font-mono">RESPUESTA MEDIA</div>
            </div>
            <div className="p-8">
              <div className="text-5xl font-black text-primary mb-2">50+</div>
              <div className="text-zinc-400 font-mono">GÉNEROS MUSICALES</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#111]">
        <div className="max-w-4xl mx-auto text-center">
          <Guitar className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-6">
            ¿PREPARADOS PARA TOCAR?
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Registra tu banda hoy y empieza a contactar con locales que buscan música como la vuestra.
          </p>
          <Link to="/register?type=band">
            <Button className="bg-primary hover:bg-red-700 text-white font-mono text-lg px-12 py-6">
              REGISTRAR MI BANDA GRATIS
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-black text-white">ROCK<span className="text-primary">MAPS</span></span>
          </div>
          <div className="flex gap-6 text-zinc-500 font-mono text-sm">
            <Link to="/para-bares" className="hover:text-primary">PARA BARES</Link>
            <Link to="/events" className="hover:text-primary">EVENTOS</Link>
            <Link to="/bars" className="hover:text-primary">BARES</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
